import type { CreateMealInput } from '../api/endpoints';

/**
 * RF-49 · la cola de comidas registradas sin conexión.
 *
 * Vive en `localStorage` y no en memoria porque el caso real es el peor: la persona registra el
 * almuerzo en el subte, cierra la aplicación y el teléfono descarta la pestaña. Si la cola no
 * sobrevive a eso, no sirve para nada.
 *
 * Cada entrada lleva un `clientMealId` estable. Ese identificador es lo que hace que reintentar
 * la sincronización no duplique la comida: el servidor lo usa como clave de idempotencia.
 */
const STORAGE_KEY = 'fitlog.pending-meals.v1';

export interface QueuedMeal {
  clientMealId: string;
  queuedAt: string;
  meal: CreateMealInput;
}

function newClientMealId(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Sin `randomUUID` la cola seguiría siendo idempotente, pero con menos entropía.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function read(): QueuedMeal[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedMeal[]) : [];
  } catch {
    // Una cola ilegible es peor que una vacía: se descarta y se avisa por el canal de siempre.
    localStorage.removeItem(STORAGE_KEY);
    return [];
  }
}

function write(entries: QueuedMeal[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

type Listener = (entries: QueuedMeal[]) => void;
const listeners = new Set<Listener>();

function notify(): void {
  const snapshot = read();
  for (const listener of listeners) {
    listener(snapshot);
  }
}

export const mealQueue = {
  list: read,

  /** Devuelve la entrada encolada para que la pantalla la pueda mostrar como pendiente. */
  enqueue(meal: CreateMealInput): QueuedMeal {
    const entry: QueuedMeal = {
      clientMealId: meal.clientMealId ?? newClientMealId(),
      queuedAt: new Date().toISOString(),
      meal,
    };

    entry.meal = { ...meal, clientMealId: entry.clientMealId };
    write([...read(), entry]);
    notify();
    return entry;
  },

  remove(clientMealId: string): void {
    write(read().filter((entry) => entry.clientMealId !== clientMealId));
    notify();
  },

  clear(): void {
    write([]);
    notify();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
